import React, { useRef, useState, useEffect } from 'react';
import '../assets/styles/Contact.scss';
import '../assets/styles/LoadingAnimations.scss';
// import emailjs from '@emailjs/browser';
import SendIcon from '@mui/icons-material/Send';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { TechStackVisualization, CodeSnippetVisualization, FeatureListVisualization } from './visualizations';
import { trackSocialClick, trackChatOpen } from '../utils/analytics';

function Contact({ parentToChild, chatProps }: {
  parentToChild?: { mode: string },
  chatProps?: {
    isChatOpen: boolean;
    setIsChatOpen: React.Dispatch<React.SetStateAction<boolean>>;
    chatMessages: any[];
    setChatMessages: React.Dispatch<React.SetStateAction<any[]>>;
    chatInput: string;
    setChatInput: React.Dispatch<React.SetStateAction<string>>;
    isTyping: boolean;
    showSuggestions: boolean;
    setShowSuggestions: React.Dispatch<React.SetStateAction<boolean>>;
    suggestedQuestions: string[];
    handleSuggestionClick: (question: string) => void;
    handleSendMessage: (message: string) => void;
    currentLoadingMessage: string;
  }
}) {
  const mode = parentToChild?.mode || 'dark';
  const isDarkMode = mode === 'dark';

  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [hiddenVisualizations, setHiddenVisualizations] = useState<Record<string, boolean>>({});

  const toggleVisualization = (messageId: string) => {
    setHiddenVisualizations(prev => ({ ...prev, [messageId]: !prev[messageId] }));
  };
  
  // Chatbot state from props
  const {
    isChatOpen,
    setIsChatOpen,
    chatMessages,
    chatInput,
    setChatInput,
    isTyping,
    showSuggestions,
    suggestedQuestions,
    handleSuggestionClick,
    handleSendMessage,
    currentLoadingMessage
  } = chatProps || {
    isChatOpen: false,
    setIsChatOpen: () => {},
    chatMessages: [],
    chatInput: '',
    setChatInput: () => {},
    isTyping: false,
    showSuggestions: true,
    suggestedQuestions: [],
    handleSuggestionClick: () => {},
    handleSendMessage: () => {},
    currentLoadingMessage: ''
  };

  // Render message content with clickable suggestions and interactive elements
  const renderMessageContent = (message: any) => {
    const content = typeof message === 'string' ? message : message.content || '';
    const followUpQuestions = message.followUpQuestions || [];
    const interactiveElement = message.interactiveElement;
    const isStreaming = message.isStreaming;

    // Check if this is an initial introduction message with bullet points
    const hasBulletQuestions = content.includes("• What tech stack was used?") && content.includes("Here are some questions you can ask:");

    // Handle interactive elements
    const renderInteractiveElement = () => {
      if (!interactiveElement) return null;

      // Enhanced logic to determine if visualization is necessary
      const isVisualizationNecessary = () => {
        // If assistant explicitly says no interactive content is needed
        if (interactiveElement.type === 'text' &&
            (interactiveElement.content === 'No interactive content required.' ||
             interactiveElement.content === 'No visualization needed.' ||
             interactiveElement.content === '' ||
             interactiveElement.content === 'None' ||
             interactiveElement.content === 'N/A')) {
          return false;
        }

        // If it's just a text type with minimal or generic content, skip visualization
        if (interactiveElement.type === 'text') {
          const textContent = interactiveElement.content?.toLowerCase() || '';
          const metadata = interactiveElement.metadata || {};
          
          // Skip if content is too generic or minimal
          if (textContent.length < 10 || 
              textContent.includes('simple response') ||
              textContent.includes('basic answer') ||
              textContent.includes('general information') ||
              (Object.keys(metadata).length === 0 && textContent.length < 20)) {
            return false;
          }
        }

        // Check for empty or minimal metadata in complex types
        if (interactiveElement.type !== 'text') {
          const metadata = interactiveElement.metadata || {};
          if (Object.keys(metadata).length === 0) {
            return false;
          }
          
          // For specific types, check if they have meaningful data
          if (interactiveElement.type === 'tech_stack' && 
              (!metadata.technologies || metadata.technologies.length === 0)) {
            return false;
          }
          
          if (interactiveElement.type === 'timeline' && 
              (!metadata.events || metadata.events.length === 0)) {
            return false;
          }
        }

        return true;
      };

      // Return null if visualization is not necessary
      if (!isVisualizationNecessary()) {
        return null;
      }

      // For 'text' interactiveElement we simply render the content as-is.
      if (interactiveElement.type === 'text') {
        return (
          <div style={{ margin: '12px 0', color: isDarkMode ? '#cbd5e1' : '#374151' }}>
            {interactiveElement.content}
          </div>
        );
      }

  // allow collapsing per-message visualizations
      const messageId = message.id || (message.timestamp ? String(message.timestamp) : undefined);
      const isHidden = messageId ? !!hiddenVisualizations[messageId] : false;

      const renderInner = () => {
  switch (interactiveElement.type) {
        case 'tech_stack':
          return (
            <TechStackVisualization
              technologies={interactiveElement.metadata?.technologies || []}
              title={interactiveElement.content}
              isDarkMode={isDarkMode}
            />
          );

        case 'timeline':
          return (
            <div className="timeline-visualization" style={{
              background: isDarkMode ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: '16px',
              padding: '20px',
              boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{
                margin: '0 0 20px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: isDarkMode ? '#f3e8ff' : '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  boxShadow: '0 0 8px rgba(168, 85, 247, 0.5)'
                }} />
                {interactiveElement.content}
              </h4>

              <div style={{ position: 'relative', paddingLeft: '28px' }}>
                {/* Vertical connecting line */}
                <div style={{
                  position: 'absolute',
                  left: '5px',
                  top: '6px',
                  bottom: '6px',
                  width: '2px',
                  background: isDarkMode
                    ? 'linear-gradient(180deg, rgba(168, 85, 247, 0.6) 0%, rgba(124, 58, 237, 0.2) 100%)'
                    : 'linear-gradient(180deg, rgba(124, 58, 237, 0.5) 0%, rgba(124, 58, 237, 0.1) 100%)',
                  borderRadius: '1px'
                }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {interactiveElement.metadata?.events?.map((event: any, index: number) => {
                    const isComplete = event.progress === 100;
                    const isInProgress = event.progress !== undefined && event.progress > 0 && event.progress < 100;

                    return (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          animation: `fadeSlideIn 0.4s ease-out ${index * 0.1}s both`
                        }}
                      >
                        {/* Status node on the timeline */}
                        <div style={{
                          position: 'absolute',
                          left: '-28px',
                          top: '6px',
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: isComplete
                            ? 'linear-gradient(135deg, #10b981, #059669)'
                            : isInProgress
                              ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                              : isDarkMode ? 'rgba(168, 85, 247, 0.4)' : 'rgba(124, 58, 237, 0.3)',
                          border: `2px solid ${isDarkMode ? '#1f2937' : '#ffffff'}`,
                          boxShadow: isComplete
                            ? '0 0 12px rgba(16, 185, 129, 0.5)'
                            : isInProgress
                              ? '0 0 12px rgba(245, 158, 11, 0.5)'
                              : '0 0 8px rgba(168, 85, 247, 0.3)',
                          zIndex: 1
                        }} />

                        {/* Event card */}
                        <div style={{
                          background: isDarkMode ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                          borderRadius: '12px',
                          padding: '14px 16px',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}>
                          {/* Phase header */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '8px',
                            flexWrap: 'wrap',
                            gap: '8px'
                          }}>
                            <div style={{
                              fontWeight: 600,
                              fontSize: '14px',
                              color: isDarkMode ? '#f3e8ff' : '#111827'
                            }}>
                              {event.phase}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {event.duration && (
                                <span style={{
                                  fontSize: '12px',
                                  color: isDarkMode ? '#a78bfa' : '#7c3aed',
                                  fontWeight: 500
                                }}>
                                  {event.duration}
                                </span>
                              )}
                              {event.progress !== undefined && (
                                <span style={{
                                  fontSize: '11px',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  fontWeight: 600,
                                  background: isComplete
                                    ? (isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)')
                                    : (isDarkMode ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.1)'),
                                  color: isComplete
                                    ? (isDarkMode ? '#34d399' : '#059669')
                                    : (isDarkMode ? '#fbbf24' : '#d97706')
                                }}>
                                  {event.progress}%
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Description */}
                          <div style={{
                            fontSize: '13px',
                            lineHeight: 1.5,
                            color: isDarkMode ? '#cbd5e1' : '#4b5563',
                            marginBottom: event.progress !== undefined ? '12px' : '0'
                          }}>
                            {event.description}
                          </div>

                          {/* Progress bar with glow */}
                          {event.progress !== undefined && (
                            <div style={{
                              height: '4px',
                              background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                              borderRadius: '2px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${event.progress}%`,
                                background: isComplete
                                  ? 'linear-gradient(90deg, #10b981, #34d399)'
                                  : 'linear-gradient(90deg, #a855f7, #c084fc)',
                                borderRadius: '2px',
                                boxShadow: isComplete
                                  ? '0 0 8px rgba(16, 185, 129, 0.5)'
                                  : '0 0 8px rgba(168, 85, 247, 0.4)',
                                transition: 'width 0.5s ease-out'
                              }} />
                            </div>
                          )}

                          {/* Deliverables */}
                          {event.deliverables && event.deliverables.length > 0 && (
                            <div style={{
                              display: 'flex',
                              gap: '6px',
                              flexWrap: 'wrap',
                              marginTop: '10px'
                            }}>
                              {event.deliverables.map((deliverable: string, deliverableIndex: number) => (
                                <span
                                  key={deliverableIndex}
                                  style={{
                                    fontSize: '10px',
                                    padding: '3px 8px',
                                    borderRadius: '8px',
                                    background: isDarkMode ? 'rgba(168, 85, 247, 0.15)' : 'rgba(124, 58, 237, 0.08)',
                                    color: isDarkMode ? '#c4b5fd' : '#7c3aed',
                                    fontWeight: 500
                                  }}
                                >
                                  {deliverable}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );

        case 'code_snippet':
          return (
            <CodeSnippetVisualization
              title={interactiveElement.content}
              language={interactiveElement.metadata?.language || 'text'}
              code={interactiveElement.metadata?.code || ''}
              explanation={interactiveElement.metadata?.explanation}
              highlights={interactiveElement.metadata?.highlights}
              performance_note={interactiveElement.metadata?.performance_note}
              github_url={interactiveElement.metadata?.github_url}
              isDarkMode={isDarkMode}
            />
          );

        case 'feature_list':
          return (
            <FeatureListVisualization
              features={interactiveElement.metadata?.features || []}
              title={interactiveElement.content}
              isDarkMode={isDarkMode}
              layout={interactiveElement.metadata?.layout || 'grid'}
            />
          );

        case 'feature_highlight': {
          const metadata = interactiveElement.metadata || {};
          const content = interactiveElement.content || '';

          // Container style (glassmorphic)
          const containerStyle: React.CSSProperties = {
            background: isDarkMode ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            borderRadius: '16px',
            padding: '20px',
            boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
          };

          // Check if this is the structured format with title/challenge/solution/impact
          if (metadata.title || metadata.challenge || metadata.solution || metadata.impact) {
            return (
              <div className="feature-highlight-visualization" style={containerStyle}>
                {/* Header with icon */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: isDarkMode
                      ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(124, 58, 237, 0.1))'
                      : 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(168, 85, 247, 0.05))',
                    border: `1px solid ${isDarkMode ? 'rgba(168, 85, 247, 0.3)' : 'rgba(124, 58, 237, 0.2)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 16px rgba(168, 85, 247, 0.2)',
                    overflow: 'hidden'
                  }}>
                    {metadata.icon_url ? (
                      <img src={metadata.icon_url} alt="feature" style={{ width: 24, height: 24, borderRadius: 4 }} />
                    ) : (
                      <span style={{ fontSize: '20px' }}>✨</span>
                    )}
                  </div>
                  <div>
                    <div style={{
                      fontWeight: 700,
                      fontSize: '15px',
                      color: isDarkMode ? '#f3e8ff' : '#111827'
                    }}>
                      {metadata.title || content}
                    </div>
                  </div>
                </div>

                {/* Three columns: Challenge | Solution | Impact */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '10px'
                }}>
                  {metadata.challenge && (
                    <div style={{
                      padding: '12px',
                      background: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(249, 250, 251, 0.8)',
                      borderRadius: '10px',
                      borderLeft: `3px solid ${isDarkMode ? '#f59e0b' : '#d97706'}`,
                      animation: 'fadeSlideIn 0.3s ease-out'
                    }}>
                      <div style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: isDarkMode ? '#fbbf24' : '#d97706',
                        marginBottom: '6px'
                      }}>Challenge</div>
                      <div style={{
                        fontSize: '12px',
                        lineHeight: 1.4,
                        color: isDarkMode ? '#e5e7eb' : '#374151'
                      }}>{metadata.challenge}</div>
                    </div>
                  )}
                  {metadata.solution && (
                    <div style={{
                      padding: '12px',
                      background: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(249, 250, 251, 0.8)',
                      borderRadius: '10px',
                      borderLeft: `3px solid ${isDarkMode ? '#a855f7' : '#7c3aed'}`,
                      animation: 'fadeSlideIn 0.3s ease-out 0.1s both'
                    }}>
                      <div style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: isDarkMode ? '#c4b5fd' : '#7c3aed',
                        marginBottom: '6px'
                      }}>Solution</div>
                      <div style={{
                        fontSize: '12px',
                        lineHeight: 1.4,
                        color: isDarkMode ? '#e5e7eb' : '#374151'
                      }}>{metadata.solution}</div>
                    </div>
                  )}
                  {metadata.impact && (
                    <div style={{
                      padding: '12px',
                      background: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(249, 250, 251, 0.8)',
                      borderRadius: '10px',
                      borderLeft: `3px solid ${isDarkMode ? '#10b981' : '#059669'}`,
                      animation: 'fadeSlideIn 0.3s ease-out 0.2s both'
                    }}>
                      <div style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: isDarkMode ? '#34d399' : '#059669',
                        marginBottom: '6px'
                      }}>Impact</div>
                      <div style={{
                        fontSize: '12px',
                        lineHeight: 1.4,
                        color: isDarkMode ? '#e5e7eb' : '#374151'
                      }}>{metadata.impact}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // Check for structured project data in metadata
          const projectKeys = Object.keys(metadata).filter(key => {
            const item = metadata[key];
            return item && typeof item === 'object' && (item.summary || item.description);
          });

          if (projectKeys.length > 0) {
            return (
              <div className="feature-highlight-visualization" style={containerStyle}>
                <h4 style={{
                  margin: '0 0 16px 0',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: isDarkMode ? '#f3e8ff' : '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                    boxShadow: '0 0 8px rgba(168, 85, 247, 0.5)'
                  }} />
                  Project Highlights
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {projectKeys.map((projectName, index) => {
                    const project = metadata[projectName];
                    const summary = project.summary || project.description || '';
                    const tech = project.tech || project.technologies || project.stack;

                    return (
                      <div key={index} style={{
                        padding: '14px',
                        background: isDarkMode ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                        borderRadius: '10px',
                        animation: `fadeSlideIn 0.3s ease-out ${index * 0.08}s both`
                      }}>
                        <div style={{
                          fontWeight: 600,
                          fontSize: '13px',
                          color: isDarkMode ? '#f3e8ff' : '#111827',
                          marginBottom: '6px'
                        }}>
                          {projectName}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          lineHeight: 1.4,
                          color: isDarkMode ? '#cbd5e1' : '#4b5563',
                          marginBottom: tech ? '10px' : '0'
                        }}>
                          {summary}
                        </div>
                        {tech && (
                          <div style={{
                            display: 'flex',
                            gap: '4px',
                            flexWrap: 'wrap'
                          }}>
                            {tech.split(/[,;]/).map((techItem: string, techIndex: number) => (
                              <span key={techIndex} style={{
                                padding: '2px 8px',
                                borderRadius: '6px',
                                background: isDarkMode ? 'rgba(168, 85, 247, 0.15)' : 'rgba(124, 58, 237, 0.08)',
                                color: isDarkMode ? '#c4b5fd' : '#7c3aed',
                                fontSize: '10px',
                                fontWeight: 500
                              }}>
                                {techItem.trim()}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          // Fallback: just show the content in a glassmorphic container
          return (
            <div className="feature-highlight-visualization" style={containerStyle}>
              <div style={{
                fontSize: '13px',
                lineHeight: 1.5,
                color: isDarkMode ? '#cbd5e1' : '#374151',
                whiteSpace: 'pre-wrap'
              }}>
                {content}
              </div>
            </div>
          );
        }

        case 'architecture':
          return (
            <div className="architecture-visualization" style={{
              background: isDarkMode ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: '16px',
              padding: '20px',
              boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{
                margin: '0 0 16px 0',
                fontSize: '15px',
                fontWeight: 600,
                color: isDarkMode ? '#f3e8ff' : '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  boxShadow: '0 0 8px rgba(168, 85, 247, 0.5)'
                }} />
                {interactiveElement.content}
              </h4>

              {interactiveElement.metadata?.image_url && (
                <img
                  src={interactiveElement.metadata.image_url}
                  alt="architecture diagram"
                  style={{
                    width: '100%',
                    borderRadius: '12px',
                    marginBottom: '16px',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`
                  }}
                />
              )}

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                position: 'relative'
              }}>
                {/* Vertical connecting line */}
                {interactiveElement.metadata?.components?.length > 1 && (
                  <div style={{
                    position: 'absolute',
                    left: '19px',
                    top: '24px',
                    bottom: '24px',
                    width: '2px',
                    background: isDarkMode
                      ? 'linear-gradient(180deg, rgba(168, 85, 247, 0.4), rgba(124, 58, 237, 0.1))'
                      : 'linear-gradient(180deg, rgba(124, 58, 237, 0.3), rgba(124, 58, 237, 0.05))',
                    zIndex: 0
                  }} />
                )}

                {interactiveElement.metadata?.components?.map((c: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'center',
                      padding: '12px',
                      background: isDarkMode ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                      border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                      borderRadius: '10px',
                      position: 'relative',
                      zIndex: 1,
                      animation: `fadeSlideIn 0.3s ease-out ${i * 0.08}s both`
                    }}
                  >
                    {/* Icon/indicator */}
                    <div style={{
                      width: '38px',
                      height: '38px',
                      borderRadius: '10px',
                      background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(124, 58, 237, 0.1))'
                        : 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(168, 85, 247, 0.05))',
                      border: `1px solid ${isDarkMode ? 'rgba(168, 85, 247, 0.3)' : 'rgba(124, 58, 237, 0.2)'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 0 12px rgba(168, 85, 247, 0.15)'
                    }}>
                      {c.icon_url ? (
                        <img
                          src={c.icon_url}
                          alt={c.name}
                          style={{ width: 20, height: 20, borderRadius: 4 }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '3px',
                          background: 'linear-gradient(135deg, #a855f7, #7c3aed)'
                        }} />
                      )}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600,
                        fontSize: '13px',
                        color: isDarkMode ? '#f3e8ff' : '#111827',
                        marginBottom: c.role ? '2px' : '0'
                      }}>
                        {c.name}
                      </div>
                      {c.role && (
                        <div style={{
                          fontSize: '12px',
                          color: isDarkMode ? '#94a3b8' : '#6b7280',
                          lineHeight: 1.3
                        }}>
                          {c.role}
                        </div>
                      )}
                    </div>

                    {/* Layer indicator */}
                    <div style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      borderRadius: '6px',
                      background: isDarkMode ? 'rgba(168, 85, 247, 0.15)' : 'rgba(124, 58, 237, 0.08)',
                      color: isDarkMode ? '#c4b5fd' : '#7c3aed',
                      fontWeight: 600
                    }}>
                      L{i + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

        case 'metrics':
          return (
            <div className="metrics-visualization" style={{
              background: isDarkMode ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: '16px',
              padding: '20px',
              boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{
                margin: '0 0 16px 0',
                fontSize: '15px',
                fontWeight: 600,
                color: isDarkMode ? '#f3e8ff' : '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  boxShadow: '0 0 8px rgba(168, 85, 247, 0.5)'
                }} />
                {interactiveElement.content}
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px'
              }}>
                {interactiveElement.metadata?.metrics?.map((m: any, i: number) => {
                  const hasDelta = m.delta !== undefined && m.delta !== null && m.delta !== '';
                  const deltaValue = typeof m.delta === 'string' ? m.delta : String(m.delta);
                  const isPositive = deltaValue.startsWith('+') || (!deltaValue.startsWith('-') && parseFloat(deltaValue) > 0);
                  const isNegative = deltaValue.startsWith('-') || parseFloat(deltaValue) < 0;

                  return (
                    <div
                      key={i}
                      style={{
                        background: isDarkMode ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center',
                        animation: `fadeSlideIn 0.3s ease-out ${i * 0.1}s both`
                      }}
                    >
                      {/* Large metric value */}
                      <div style={{
                        fontSize: '28px',
                        fontWeight: 800,
                        background: isDarkMode
                          ? 'linear-gradient(135deg, #f3e8ff, #c4b5fd)'
                          : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        lineHeight: 1.1,
                        marginBottom: '2px'
                      }}>
                        {m.value}
                        {m.unit && (
                          <span style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginLeft: '2px',
                            opacity: 0.7
                          }}>
                            {m.unit}
                          </span>
                        )}
                      </div>

                      {/* Label */}
                      <div style={{
                        fontSize: '12px',
                        color: isDarkMode ? '#94a3b8' : '#6b7280',
                        fontWeight: 500,
                        marginBottom: hasDelta ? '8px' : '0'
                      }}>
                        {m.label}
                      </div>

                      {/* Delta indicator */}
                      {hasDelta && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          padding: '3px 8px',
                          borderRadius: '8px',
                          background: isPositive
                            ? (isDarkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)')
                            : isNegative
                              ? (isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)')
                              : (isDarkMode ? 'rgba(148, 163, 184, 0.15)' : 'rgba(148, 163, 184, 0.1)'),
                          color: isPositive
                            ? (isDarkMode ? '#34d399' : '#059669')
                            : isNegative
                              ? (isDarkMode ? '#f87171' : '#dc2626')
                              : (isDarkMode ? '#94a3b8' : '#6b7280')
                        }}>
                          <span style={{ fontSize: '10px' }}>
                            {isPositive ? '▲' : isNegative ? '▼' : '•'}
                          </span>
                          {deltaValue}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );

        case 'demo':
          return (
            <div style={{ margin: '12px 0' }}>
              <h4 style={{ margin: '0 0 8px 0', color: isDarkMode ? '#e9d5ff' : '#7c3aed' }}>{interactiveElement.content}</h4>
              {interactiveElement.metadata?.video_url ? (
                <video controls style={{ width: '100%', borderRadius: 8 }} src={interactiveElement.metadata.video_url} />
              ) : interactiveElement.metadata?.gif_url ? (
                <img src={interactiveElement.metadata.gif_url} alt="demo" style={{ width: '100%', borderRadius: 8 }} />
              ) : null}
            </div>
          );

        case 'contributors':
          return (
            <div style={{ margin: '12px 0' }}>
              <h4 style={{ margin: '0 0 8px 0', color: isDarkMode ? '#e9d5ff' : '#7c3aed' }}>{interactiveElement.content}</h4>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {interactiveElement.metadata?.people?.map((p: any, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 10px', borderRadius: 10, background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#fff' }}>
                    {p.avatar_url ? <img src={p.avatar_url} alt={p.name} style={{ width: 36, height: 36, borderRadius: 8 }} /> : <div style={{ width: 36, height: 36, borderRadius: 8, background: '#ddd' }} />}
                    <div>
                      <div style={{ fontWeight: 700 }}>{p.name}</div>
                      <div style={{ fontSize: 13, color: isDarkMode ? '#cbd5e1' : '#6b7280' }}>{p.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

        case 'links':
          return (
            <div className="links-visualization" style={{
              background: isDarkMode ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: '16px',
              padding: '20px',
              boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{
                margin: '0 0 14px 0',
                fontSize: '15px',
                fontWeight: 600,
                color: isDarkMode ? '#f3e8ff' : '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  boxShadow: '0 0 8px rgba(168, 85, 247, 0.5)'
                }} />
                {interactiveElement.content}
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {interactiveElement.metadata?.links?.map((l: any, i: number) => {
                  // Extract domain from URL
                  let domain = '';
                  try {
                    domain = new URL(l.url).hostname.replace('www.', '');
                  } catch {
                    domain = l.url;
                  }

                  return (
                    <a
                      key={i}
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackSocialClick(`external_link_${domain}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 14px',
                        background: isDarkMode ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                        borderRadius: '10px',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease',
                        animation: `fadeSlideIn 0.3s ease-out ${i * 0.08}s both`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = isDarkMode
                          ? '0 6px 20px rgba(168, 85, 247, 0.15)'
                          : '0 6px 20px rgba(124, 58, 237, 0.1)';
                        e.currentTarget.style.borderColor = isDarkMode
                          ? 'rgba(168, 85, 247, 0.3)'
                          : 'rgba(124, 58, 237, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = isDarkMode
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.06)';
                      }}
                    >
                      {/* Link icon */}
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: isDarkMode
                          ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(124, 58, 237, 0.1))'
                          : 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(168, 85, 247, 0.05))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <span style={{ fontSize: '14px' }}>🔗</span>
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: 600,
                          fontSize: '13px',
                          color: isDarkMode ? '#f3e8ff' : '#111827',
                          marginBottom: '2px'
                        }}>
                          {l.title}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: isDarkMode ? '#94a3b8' : '#6b7280',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {domain}
                        </div>
                      </div>

                      {/* External indicator */}
                      <div style={{
                        fontSize: '12px',
                        color: isDarkMode ? '#a78bfa' : '#7c3aed'
                      }}>
                        ↗
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          );

        case 'roadmap':
          return (
            <div className="roadmap-visualization" style={{
              background: isDarkMode ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: '16px',
              padding: '20px',
              boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <h4 style={{
                margin: '0 0 16px 0',
                fontSize: '15px',
                fontWeight: 600,
                color: isDarkMode ? '#f3e8ff' : '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  boxShadow: '0 0 8px rgba(168, 85, 247, 0.5)'
                }} />
                {interactiveElement.content}
              </h4>

              {/* Horizontal timeline with nodes */}
              <div style={{ position: 'relative' }}>
                {/* Timeline line */}
                <div style={{
                  position: 'absolute',
                  left: '6px',
                  top: '6px',
                  bottom: '6px',
                  width: '2px',
                  background: isDarkMode
                    ? 'linear-gradient(180deg, rgba(168, 85, 247, 0.5), rgba(124, 58, 237, 0.15))'
                    : 'linear-gradient(180deg, rgba(124, 58, 237, 0.4), rgba(124, 58, 237, 0.1))'
                }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingLeft: '24px' }}>
                  {interactiveElement.metadata?.items?.map((it: any, i: number) => {
                    const isPast = it.status === 'past' || it.status === 'completed';
                    const isCurrent = it.status === 'current' || it.status === 'in-progress';

                    return (
                      <div
                        key={i}
                        style={{
                          position: 'relative',
                          animation: `fadeSlideIn 0.3s ease-out ${i * 0.1}s both`
                        }}
                      >
                        {/* Status node */}
                        <div style={{
                          position: 'absolute',
                          left: '-24px',
                          top: '4px',
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: isPast
                            ? 'linear-gradient(135deg, #10b981, #059669)'
                            : isCurrent
                              ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                              : isDarkMode ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.4)',
                          border: `2px solid ${isDarkMode ? '#1f2937' : '#ffffff'}`,
                          boxShadow: isPast
                            ? '0 0 10px rgba(16, 185, 129, 0.4)'
                            : isCurrent
                              ? '0 0 10px rgba(245, 158, 11, 0.4), 0 0 20px rgba(245, 158, 11, 0.2)'
                              : 'none',
                          animation: isCurrent ? 'pulse 2s infinite' : 'none'
                        }} />

                        {/* Content card */}
                        <div style={{
                          padding: '12px 14px',
                          background: isDarkMode ? 'rgba(31, 41, 55, 0.6)' : 'rgba(255, 255, 255, 0.9)',
                          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                          borderRadius: '10px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '6px',
                            flexWrap: 'wrap',
                            gap: '8px'
                          }}>
                            <div style={{
                              fontWeight: 600,
                              fontSize: '13px',
                              color: isDarkMode ? '#f3e8ff' : '#111827'
                            }}>
                              {it.milestone}
                            </div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              {it.eta && (
                                <span style={{
                                  fontSize: '10px',
                                  padding: '2px 8px',
                                  borderRadius: '8px',
                                  fontWeight: 600,
                                  background: isPast
                                    ? (isDarkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)')
                                    : isCurrent
                                      ? (isDarkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)')
                                      : (isDarkMode ? 'rgba(148, 163, 184, 0.15)' : 'rgba(148, 163, 184, 0.1)'),
                                  color: isPast
                                    ? (isDarkMode ? '#34d399' : '#059669')
                                    : isCurrent
                                      ? (isDarkMode ? '#fbbf24' : '#d97706')
                                      : (isDarkMode ? '#94a3b8' : '#6b7280')
                                }}>
                                  {it.eta}
                                </span>
                              )}
                            </div>
                          </div>
                          {it.details && (
                            <div style={{
                              fontSize: '12px',
                              lineHeight: 1.4,
                              color: isDarkMode ? '#94a3b8' : '#6b7280'
                            }}>
                              {it.details}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );

        case 'skills': {
          const skills = interactiveElement.metadata?.skills || [];
          return (
            <div style={{ margin: '12px 0' }}>
              <h4 style={{ margin: '0 0 8px 0', color: isDarkMode ? '#e9d5ff' : '#7c3aed' }}>{interactiveElement.content}</h4>
              {skills.length === 0 ? null : (
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <svg width={220} height={220} viewBox="0 0 220 220">
                    <g transform="translate(110,110)">
                      {[20,40,60,80,100].map((scale) => {
                        const points = skills.map((_: any, i: number) => {
                          const angle = (Math.PI * 2 * i) / skills.length - Math.PI / 2;
                          const r = 80 * (scale / 100);
                          const x = Math.cos(angle) * r;
                          const y = Math.sin(angle) * r;
                          return `${x},${y}`;
                        }).join(' ');
                        return <polygon key={scale} points={points} fill="none" stroke={isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.06)'} strokeWidth={1} />;
                      })}
                      {(function(){
                        const points = skills.map((s: any, i: number) => {
                          const angle = (Math.PI * 2 * i) / skills.length - Math.PI / 2;
                          const r = 80 * ((s.score || 0) / 100);
                          const x = Math.cos(angle) * r;
                          const y = Math.sin(angle) * r;
                          return `${x},${y}`;
                        }).join(' ');
                        return <polygon points={points} fill={isDarkMode ? 'rgba(124,58,237,0.18)' : 'rgba(124,58,237,0.12)'} stroke={isDarkMode ? '#c4b5fd' : '#7c3aed'} strokeWidth={1.5} />;
                      })()}
                      {skills.map((s: any, i: number) => {
                        const angle = (Math.PI * 2 * i) / skills.length - Math.PI / 2;
                        const x = Math.cos(angle) * 92;
                        const y = Math.sin(angle) * 92;
                        return (
                          <text key={i} x={x} y={y} fontSize={10} textAnchor={x > 20 ? 'start' : x < -20 ? 'end' : 'middle'} fill={isDarkMode ? '#e6e6f0' : '#374151'}>
                            {s.label}
                          </text>
                        );
                      })}
                    </g>
                  </svg>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {skills.map((s: any, i: number) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: isDarkMode ? '#c4b5fd' : '#7c3aed' }} />
                        <div style={{ fontSize: 13, color: isDarkMode ? '#cbd5e1' : '#374151' }}>{s.label}: <strong style={{ color: isDarkMode ? '#f3e8ff' : '#111827' }}>{s.score}</strong></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        }

        case 'case_study': {
          const meta = interactiveElement.metadata || {};
          const hasMetric = meta.metric && meta.metric.before && meta.metric.after;

          return (
            <div className="case-study-visualization" style={{
              background: isDarkMode ? 'rgba(17, 24, 39, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: '16px',
              padding: '20px',
              boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              {/* Title */}
              <h4 style={{
                margin: '0 0 16px 0',
                fontSize: '15px',
                fontWeight: 600,
                color: isDarkMode ? '#f3e8ff' : '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  boxShadow: '0 0 8px rgba(168, 85, 247, 0.5)'
                }} />
                {meta.title || interactiveElement.content}
              </h4>

              {/* Three connected cards: Problem → Solution → Result */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '8px',
                position: 'relative'
              }}>
                {/* Problem Card */}
                {meta.problem && (
                  <div style={{
                    background: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                    border: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)'}`,
                    borderRadius: '10px',
                    padding: '12px',
                    animation: 'fadeSlideIn 0.3s ease-out'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '14px' }}>⚠️</span>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: isDarkMode ? '#f87171' : '#dc2626'
                      }}>Problem</span>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      lineHeight: 1.4,
                      color: isDarkMode ? '#e5e7eb' : '#374151'
                    }}>
                      {meta.problem}
                    </div>
                  </div>
                )}

                {/* Solution Card */}
                {meta.solution && (
                  <div style={{
                    background: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                    border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)'}`,
                    borderRadius: '10px',
                    padding: '12px',
                    animation: 'fadeSlideIn 0.3s ease-out 0.1s both'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '14px' }}>💡</span>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: isDarkMode ? '#60a5fa' : '#2563eb'
                      }}>Solution</span>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      lineHeight: 1.4,
                      color: isDarkMode ? '#e5e7eb' : '#374151'
                    }}>
                      {meta.solution}
                    </div>
                  </div>
                )}

                {/* Result Card */}
                {meta.result && (
                  <div style={{
                    background: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)',
                    border: `1px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)'}`,
                    borderRadius: '10px',
                    padding: '12px',
                    animation: 'fadeSlideIn 0.3s ease-out 0.2s both'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '14px' }}>✅</span>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: isDarkMode ? '#34d399' : '#059669'
                      }}>Result</span>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      lineHeight: 1.4,
                      color: isDarkMode ? '#e5e7eb' : '#374151'
                    }}>
                      {meta.result}
                    </div>
                  </div>
                )}
              </div>

              {/* Metric comparison bar */}
              {hasMetric && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(249, 250, 251, 0.8)',
                  borderRadius: '10px',
                  animation: 'fadeSlideIn 0.3s ease-out 0.3s both'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: isDarkMode ? '#94a3b8' : '#6b7280',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {meta.metric.label}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '11px',
                        marginBottom: '4px',
                        color: isDarkMode ? '#94a3b8' : '#6b7280'
                      }}>
                        <span>Before: {meta.metric.before}</span>
                        <span>After: {meta.metric.after}</span>
                      </div>
                      <div style={{
                        height: '8px',
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <div style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: '100%',
                          background: 'linear-gradient(90deg, #10b981, #34d399)',
                          borderRadius: '4px',
                          boxShadow: '0 0 8px rgba(16, 185, 129, 0.4)'
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        }

          case 'contact': {
            // Always show hardcoded contact buttons: LinkedIn, Email, GitHub
            const items = [
              { id: 'linkedin', title: 'LinkedIn', subtitle: "Let's connect", link: 'https://www.linkedin.com/in/yanivvds/', color: '#0A66C2' },
              { id: 'email', title: 'Email', subtitle: 'Drop me a line', link: 'mailto:yanivvds@gmail.com', color: '#ffffffff' },
              { id: 'github', title: 'GitHub', subtitle: 'Check out my code', link: 'https://github.com/yanivvds', color: '#111827' }
            ];

            return (
              <div style={{ margin: '12px 0' }}>
                <h4 style={{ margin: '0 0 8px 0', color: isDarkMode ? '#e9d5ff' : '#7c3aed' }}>{interactiveElement.content || 'Contact'}</h4>
                <div className="chat-contact-visualization">
                  {items.map((c: any, i: number) => (
                    <a
                      key={i}
                      className="chat-contact-button"
                      href={c.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={c.title}
                      onClick={() => trackSocialClick(c.id)}
                      style={{ ['--card-color' as any]: c.color || (isDarkMode ? '#8b5cf6' : '#7c3aed') } as React.CSSProperties & { [key: string]: string }}
                    >
                      <div className="icon-wrap" style={{ background: c.color }}>
                        {c.id === 'linkedin' ? (
                          <LinkedInIcon style={{ fontSize: 28, color: '#fff' }} />
                        ) : c.id === 'email' ? (
                          // Use a white Gmail SVG icon so it shows on the red background
                          <img src="https://img.icons8.com/?size=100&id=P7UIlhbpWzZm&format=png&color=000000" alt="Gmail" style={{ width: 28, height: 28 }} />
                        ) : c.id === 'github' ? (
                          <GitHubIcon style={{ fontSize: 28, color: '#fff' }} />
                        ) : (
                          <SendIcon style={{ fontSize: 28 }} />
                        )}
                      </div>
                      <div className="label-wrap">
                        <div className="label">{c.title}</div>
                        <div className="sub">{c.subtitle}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            );
          }

        default:
          // graceful fallback: pretty-print metadata or content so we still show something useful
          return (
            <div style={{
              margin: '12px 0',
              padding: '12px',
              background: isDarkMode ? 'rgba(139, 92, 246, 0.06)' : 'rgba(124, 58, 237, 0.04)',
              borderRadius: '8px',
              border: `1px solid ${isDarkMode ? 'rgba(139, 92, 246, 0.12)' : 'rgba(124, 58, 237, 0.08)'}`,
              fontSize: '13px',
              color: isDarkMode ? '#cbd5e1' : '#374151'
            }}>
              <strong style={{ display: 'block', marginBottom: '8px', color: isDarkMode ? '#8b5cf6' : '#7c3aed' }}>
                {interactiveElement.content || 'Details'}
              </strong>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(interactiveElement.metadata || {}, null, 2)}</pre>
            </div>
          );
      }
      };

      return (
        <div style={{ margin: '12px 0' }}>
          {messageId && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
              <button
                onClick={() => toggleVisualization(messageId)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: isDarkMode ? '#94a3b8' : '#4b5563',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                aria-label={isHidden ? 'Show visualization' : 'Hide visualization'}
              >
                {isHidden ? 'Show visualization' : 'Hide visualization'}
              </button>
            </div>
          )}

          {!isHidden && renderInner()}
        </div>
      );
    };

    // Function to highlight project names in purple and handle Markdown bold
    const highlightProjectName = (text: string) => {
      const projectNames = [
        'Kalff Fundraising Dashboard',
        'KPN Easy Mode',
        'VibeGroup Recruitment Algorithm',
        'TagJeTafel',
        'Travel Insider Tips',
        'Task Management API'
      ];

      let highlightedText = text;
      
      // Handle Markdown bold syntax **text** - using more specific pattern to avoid conflicts
      highlightedText = highlightedText.replace(/\*\*((?:[^*]|\*(?!\*))+)\*\*/g, '<strong style="font-weight: 700; color: inherit;">$1</strong>');
      
      // Also handle partial bold during streaming (text that has opening ** but no closing yet)
      // This helps with real-time rendering during streaming
      if (highlightedText.includes('**') && !highlightedText.match(/\*\*[^*]*\*\*/)) {
        // Match unclosed bold at the end of streaming text
        highlightedText = highlightedText.replace(/\*\*([^*]+)$/, '<strong style="font-weight: 700; color: inherit;">$1');
      }
      
      // Highlight project names
      projectNames.forEach(projectName => {
        const regex = new RegExp(`(${projectName})`, 'gi');
        highlightedText = highlightedText.replace(regex, `<span style="color: ${isDarkMode ? '#a855f7' : '#7c3aed'}; font-weight: 600;">$1</span>`);
      });

      return highlightedText;
    };

    // If this is an initial introduction message with questions, render them as buttons
    if (hasBulletQuestions) {
      const parts = content.split("Here are some questions you can ask:");
      const introText = parts[0] + "Here are some questions you can ask:";
      const questionsText = parts[1] || "";
      const questions = questionsText.split("• ").filter((q: string) => q.trim()).map((q: string) => q.replace("?", "").trim() + "?");

      return (
        <div>
          <div dangerouslySetInnerHTML={{ __html: highlightProjectName(introText) }} />
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            margin: '16px 0',
            padding: '0'
          }}>
            {questions.map((question: string, index: number) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(question)}
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.1))'
                    : 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(139, 92, 246, 0.1))',
                  border: `1px solid ${isDarkMode ? 'rgba(139, 92, 246, 0.3)' : 'rgba(124, 58, 237, 0.3)'}`,
                  borderRadius: '8px',
                  color: isDarkMode ? '#8b5cf6' : '#7c3aed',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  fontWeight: '500',
                  padding: '10px 14px',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode
                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(168, 85, 247, 0.2))'
                    : 'linear-gradient(135deg, rgba(124, 58, 237, 0.2), rgba(139, 92, 246, 0.2))';
                  e.currentTarget.style.borderColor = isDarkMode ? 'rgba(139, 92, 246, 0.5)' : 'rgba(124, 58, 237, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = isDarkMode
                    ? '0 4px 12px rgba(139, 92, 246, 0.15)'
                    : '0 4px 12px rgba(124, 58, 237, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isDarkMode
                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.1))'
                    : 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(139, 92, 246, 0.1))';
                  e.currentTarget.style.borderColor = isDarkMode ? 'rgba(139, 92, 246, 0.3)' : 'rgba(124, 58, 237, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <span>{question}</span>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: isDarkMode ? 'rgba(139, 92, 246, 0.2)' : 'rgba(124, 58, 237, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: '8px',
                  transition: 'all 0.2s ease'
                }}>
                  <ArrowForwardIcon style={{
                    fontSize: '12px',
                    color: isDarkMode ? '#8b5cf6' : '#7c3aed'
                  }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Before the first text chunk arrives, show a terminal-style status
    // line instead of an empty bubble.
    if (isStreaming && !content) {
      return (
        <div className="terminal-status" key={currentLoadingMessage}>
          <span className="terminal-status__prompt">&gt;</span>
          <span className="terminal-status__label">{currentLoadingMessage || 'thinking'}</span>
          <span className="terminal-status__cursor" />
        </div>
      );
    }

    return (
      <div>
        <div dangerouslySetInnerHTML={{ __html: highlightProjectName(content) }} />
        {isStreaming && !content.includes('Thinking') && !content.includes('...') && (
          <span style={{
            display: 'inline-block',
            width: '8px',
            height: '16px',
            background: isDarkMode ? '#5000ca' : '#6b46c1',
            borderRadius: '2px',
            marginLeft: '4px',
            animation: 'blink 1.2s infinite'
          }} />
        )}
        {message.enhancementPending && (
          <div className="terminal-status terminal-status--inline">
            <span className="terminal-status__prompt">&gt;</span>
            <span className="terminal-status__label">preparing follow-ups</span>
            <span className="terminal-status__cursor" />
          </div>
        )}
        {renderInteractiveElement()}
        {followUpQuestions.length > 0 && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            margin: '16px 0',
            padding: '0'
          }}>
            {followUpQuestions.map((question: string, index: number) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(question)}
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.1))'
                    : 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(139, 92, 246, 0.1))',
                  border: `1px solid ${isDarkMode ? 'rgba(139, 92, 246, 0.3)' : 'rgba(124, 58, 237, 0.3)'}`,
                  borderRadius: '8px',
                  color: isDarkMode ? '#8b5cf6' : '#7c3aed',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  fontWeight: '500',
                  padding: '10px 14px',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode
                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(168, 85, 247, 0.2))'
                    : 'linear-gradient(135deg, rgba(124, 58, 237, 0.2), rgba(139, 92, 246, 0.2))';
                  e.currentTarget.style.borderColor = isDarkMode ? 'rgba(139, 92, 246, 0.5)' : 'rgba(124, 58, 237, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = isDarkMode
                    ? '0 4px 12px rgba(139, 92, 246, 0.15)'
                    : '0 4px 12px rgba(124, 58, 237, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isDarkMode
                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.1))'
                    : 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(139, 92, 246, 0.1))';
                  e.currentTarget.style.borderColor = isDarkMode ? 'rgba(139, 92, 246, 0.3)' : 'rgba(124, 58, 237, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <span>{question}</span>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: isDarkMode ? 'rgba(139, 92, 246, 0.2)' : 'rgba(124, 58, 237, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: '8px',
                  transition: 'all 0.2s ease'
                }}>
                  <ArrowForwardIcon style={{
                    fontSize: '12px',
                    color: isDarkMode ? '#8b5cf6' : '#7c3aed'
                  }} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isTyping]);

  const contactOptions = [
    {
      id: 'email',
      icon: <EmailIcon />,
      title: 'Email',
      subtitle: 'Drop me a line',
      value: 'yanivvds@gmail.com',
      link: 'mailto:yanivvds@gmail.com',
      color: '#FF6B6B'
    },
    {
      id: 'linkedin',
      icon: <LinkedInIcon />,
      title: 'LinkedIn',
      subtitle: 'Let\'s connect',
      value: '/in/yanivvds',
      link: 'https://www.linkedin.com/in/yanivvds/',
      color: '#0077B5'
    },
    {
      id: 'github',
      icon: <GitHubIcon />,
      title: 'GitHub',
      subtitle: 'Check out my code',
      value: '/yanivvds',
      link: 'https://github.com/yanivvds',
      color: '#333'
    }
  ];

  const handleChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || isTyping) return;

    handleSendMessage(chatInput);
  };

  return (
    <>
      <div id="contact">
        <div className="items-container">
          <div className={`contact_wrapper ${isDarkMode ? 'dark' : 'light'}`}>
            <h1>Let's Connect</h1>
            <p>Ready to collaborate on something amazing? Reach out through any of these channels!</p>
            
            <div className="contact-cards-container">
              {contactOptions.map((option) => (
                <div
                  key={option.id}
                  className={`contact-card ${hoveredCard === option.id ? 'hovered' : ''}`}
                  onMouseEnter={() => setHoveredCard(option.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => window.open(option.link, '_blank')}
                  style={{
                    '--card-color': option.color
                  } as React.CSSProperties & { [key: string]: string }}
                >
                  <div className="contact-card-icon">
                    {option.icon}
                  </div>
                  <div className="contact-card-content">
                    <h3>{option.title}</h3>
                    <p className="contact-card-subtitle">{option.subtitle}</p>
                    <p className="contact-card-value">{option.value}</p>
                  </div>
                  <div className="contact-card-arrow">
                    <SendIcon />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="contact-cta">
              <p>Or feel free to connect with me on social media for updates on my latest projects!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chatbot Button */}
      <button 
        className={`chatbot-toggle ${isChatOpen ? 'active' : ''}`}
        onClick={() => {
          if (!isChatOpen) {
            trackChatOpen();
          }
          setIsChatOpen(!isChatOpen);
        }}
        aria-label="Open chat assistant"
      >
        {isChatOpen ? <CloseIcon /> : <ChatIcon />}
      </button>

      {/* Chatbot Modal */}
      {isChatOpen && (
        <div className="chatbot-modal-overlay" onClick={() => setIsChatOpen(false)}>
          <div className={`chatbot-modal ${mode}`} onClick={(e) => e.stopPropagation()}>
            <div className="chatbot-header">
              <h3>AI Assistant</h3>
              <button 
                className="chatbot-close"
                onClick={() => setIsChatOpen(false)}
                aria-label="Close chat"
              >
                <CloseIcon />
              </button>
            </div>
            
            <div className="chatbot-messages">
              {chatMessages.map((msg) => (
                <div key={msg.id} className={`chatbot-message ${msg.role}`}>
                  <div className="chatbot-message-content">
                    {renderMessageContent(msg)}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            
            {showSuggestions && (
              <div className="chatbot-suggestions">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    className="chatbot-suggestion-chip"
                    onClick={() => handleSuggestionClick(question)}
                    disabled={isTyping}
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}
            
            <form className="chatbot-input-form" onSubmit={handleChatSubmit}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask me about Yaniv's background..."
                disabled={isTyping}
              />
              <button 
                type="submit" 
                disabled={!chatInput.trim() || isTyping}
                aria-label="Send message"
              >
                <SendIcon />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Contact;
